import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import {
  listRMAs, getRMA, createRMA, approveRMA, rejectRMA, advanceRMAStatus, getRMASummary, getOrders, getInventory,
} from '../lib/api';
import {
  ArrowUUpLeft, Package, Plus, Check, X, Image as ImageIcon, Truck, Storefront, Warning, Clock, CheckCircle,
  XCircle, CaretRight, Upload, Sparkle,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import FilterBar from '../components/FilterBar';
import BulkActionsBar from '../components/BulkActionsBar';
import useBulkSelection from '../lib/useBulkSelection';
import { matchesCreatedWindow, CREATED_WINDOW_OPTIONS } from '../lib/dateWindow';
import useUrlFilterState, { useUrlFilterClear } from '../lib/useUrlFilterState';

const REASONS = [
  { key: 'damaged',         label: 'Damaged',         icon: Warning,       sev: 'red' },
  { key: 'incorrect_item',  label: 'Incorrect item',  icon: XCircle,       sev: 'amber' },
  { key: 'change_of_mind',  label: 'Change of mind',  icon: ArrowUUpLeft,  sev: 'slate' },
  { key: 'warranty_claim',  label: 'Warranty claim',  icon: Sparkle,       sev: 'blue' },
];

const STATUSES = ['pending', 'approved', 'rejected', 'received', 'refunded', 'completed'];
const STATUS_STYLES = {
  pending:   'bg-amber-100 text-amber-900',
  approved:  'bg-emerald-100 text-emerald-800',
  rejected:  'bg-red-100 text-red-800',
  received:  'bg-sky-100 text-sky-800',
  refunded:  'bg-blue-100 text-blue-800',
  completed: 'bg-slate-200 text-foreground',
};

function StatCard({ label, value, accent = 'slate' }) {
  const colors = {
    slate: 'text-foreground', amber: 'text-amber-700', red: 'text-red-700',
    green: 'text-emerald-700', blue: 'text-brand',
  };
  return (
    <div className="border border-border bg-card p-4">
      <p className="text-[10px] font-bold tracking-[0.2em] text-muted-foreground uppercase">{label}</p>
      <p className={`text-3xl font-bold font-mono mt-1 ${colors[accent] || colors.slate}`}>{value ?? '—'}</p>
    </div>
  );
}

async function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve({ filename: file.name, content_type: file.type, data_url: fr.result });
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

function CreateRMADialog({ open, onOpenChange, onCreated }) {
  const [orders, setOrders] = useState([]);
  const [variants, setVariants] = useState([]);
  const [orderId, setOrderId] = useState('');
  const [reason, setReason] = useState('damaged');
  const [approvalMode, setApprovalMode] = useState('auto');
  const [returnMethod, setReturnMethod] = useState('courier_pickup');
  const [pickupAddress, setPickupAddress] = useState('');
  const [dropoffLocationId, setDropoffLocationId] = useState('');
  const [items, setItems] = useState([{ variant_id: '', quantity: 1 }]);
  const [reasonNotes, setReasonNotes] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [customerEmail, setCustomerEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    getOrders().then(({ data }) => setOrders(data.slice(0, 50))).catch(() => {});
    getInventory({ limit: 50 }).then(({ data }) => setVariants(data)).catch(() => {});
  }, [open]);

  const addItem = () => setItems([...items, { variant_id: '', quantity: 1 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const patchItem = (i, patch) => setItems(items.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const MAX = 2 * 1024 * 1024;
    const valid = files.filter(f => f.size <= MAX);
    if (valid.length !== files.length) toast.error(`Some files exceed 2MB and were skipped`);
    const encoded = await Promise.all(valid.map(fileToDataURL));
    setAttachments([...attachments, ...encoded].slice(0, 6));
  };

  const handleSubmit = async () => {
    if (!items.every(i => i.variant_id && i.quantity > 0)) {
      toast.error('Every item needs a variant and quantity');
      return;
    }
    const payload = {
      order_id: orderId || null,
      customer_email: customerEmail || null,
      items,
      reason,
      reason_notes: reasonNotes || null,
      return_method: returnMethod,
      pickup_address: returnMethod === 'courier_pickup' ? pickupAddress : null,
      dropoff_location_id: returnMethod === 'dropoff' ? dropoffLocationId : null,
      attachments,
      approval_mode: approvalMode,
    };
    setSubmitting(true);
    try {
      const { data } = await createRMA(payload);
      toast.success(`RMA ${data.rma_number} created — ${data.status}`);
      onCreated(data);
      onOpenChange(false);
      // Reset form
      setItems([{ variant_id: '', quantity: 1 }]);
      setAttachments([]);
      setReasonNotes('');
      setPickupAddress('');
      setDropoffLocationId('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create RMA');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rma-create-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight">Create Return Request</DialogTitle>
          <DialogDescription>Generate an RMA number for a customer return.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Original sales order</Label>
              <Select value={orderId || 'none'} onValueChange={(v) => setOrderId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="rma-order-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No order (warranty etc.) —</SelectItem>
                  {orders.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.order_number} · {o.customer_email || 'no email'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Customer email</Label>
              <Input value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="customer@example.com" data-testid="rma-customer-email" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Return reason</Label>
            <div className="flex gap-2 flex-wrap mt-1">
              {REASONS.map(r => {
                const Icon = r.icon; const on = reason === r.key;
                return (
                  <button key={r.key} type="button" onClick={() => setReason(r.key)} data-testid={`rma-reason-${r.key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold tracking-wider uppercase border-2 ${on ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'}`}>
                    <Icon size={14} weight="fill" /> {r.label}
                  </button>
                );
              })}
            </div>
            <Textarea className="mt-2" rows={2} placeholder="Optional note about the reason" value={reasonNotes} onChange={e => setReasonNotes(e.target.value)} data-testid="rma-reason-notes" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-bold uppercase tracking-wider">Items</Label>
              <Button variant="outline" size="sm" onClick={addItem} data-testid="rma-add-item"><Plus size={12} className="mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2 border border-border bg-muted p-2" data-testid={`rma-item-${i}`}>
                  <Select value={it.variant_id} onValueChange={(v) => patchItem(i, { variant_id: v })}>
                    <SelectTrigger className="flex-1" data-testid={`rma-item-variant-${i}`}><SelectValue placeholder="Variant" /></SelectTrigger>
                    <SelectContent>
                      {variants.slice(0, 50).map(v => (
                        <SelectItem key={v.variant_id} value={v.variant_id}>{v.sku_code || v.variant_id.slice(-6)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" min={1} className="w-20" value={it.quantity} onChange={e => patchItem(i, { quantity: Number(e.target.value || 1) })} data-testid={`rma-item-qty-${i}`} />
                  {items.length > 1 && (
                    <button onClick={() => removeItem(i)} className="text-red-600 p-1" data-testid={`rma-remove-item-${i}`}><X size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Return method</Label>
            <div className="flex gap-2 mt-1">
              <button type="button" onClick={() => setReturnMethod('courier_pickup')} data-testid="rma-method-pickup"
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold tracking-wider uppercase border-2 ${returnMethod === 'courier_pickup' ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'}`}>
                <Truck size={14} /> Courier pickup
              </button>
              <button type="button" onClick={() => setReturnMethod('dropoff')} data-testid="rma-method-dropoff"
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold tracking-wider uppercase border-2 ${returnMethod === 'dropoff' ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'}`}>
                <Storefront size={14} /> Drop-off location
              </button>
            </div>
            {returnMethod === 'courier_pickup' ? (
              <Input className="mt-2" placeholder="Pickup address" value={pickupAddress} onChange={e => setPickupAddress(e.target.value)} data-testid="rma-pickup-address" />
            ) : (
              <Input className="mt-2" placeholder="Drop-off location ID" value={dropoffLocationId} onChange={e => setDropoffLocationId(e.target.value)} data-testid="rma-dropoff-id" />
            )}
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Approval mode</Label>
            <div className="flex gap-2 mt-1">
              {['auto', 'manual'].map(m => (
                <button key={m} type="button" onClick={() => setApprovalMode(m)} data-testid={`rma-approval-${m}`}
                  className={`flex-1 px-3 py-2 text-xs font-bold tracking-wider uppercase border-2 ${approvalMode === m ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'}`}>
                  {m === 'auto' ? 'Auto (rule-based)' : 'Manual approval'}
                </button>
              ))}
            </div>
            {approvalMode === 'auto' && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Fast-tracks: damaged · incorrect item · warranty claim. Change-of-mind needs order ≤ 30 days old and total ≤ $500.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Photos / attachments</Label>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold tracking-wider uppercase border-2 border-border bg-card hover:border-brand cursor-pointer">
                <Upload size={12} /> Upload
                <input type="file" multiple accept="image/*" onChange={handlePhotos} className="hidden" data-testid="rma-photo-input" />
              </label>
              <span className="text-[11px] text-muted-foreground">{attachments.length}/6 · Max 2MB each</span>
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {attachments.map((a, i) => (
                  <div key={i} className="relative border border-border w-16 h-16 bg-muted" data-testid={`rma-attachment-${i}`}>
                    <img src={a.data_url} alt={a.filename} className="w-full h-full object-cover" />
                    <button onClick={() => setAttachments(attachments.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 bg-red-600 text-white p-0.5">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rma-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="rma-submit-btn" className="bg-brand hover:bg-[#001F8A]">
            {submitting ? 'Creating…' : 'Create RMA'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RMADetailDialog({ rmaId, onOpenChange, onUpdated }) {
  const [rma, setRma] = useState(null);
  const [note, setNote] = useState('');
  const [restockLocationId, setRestockLocationId] = useState('');
  const [inspectionDamaged, setInspectionDamaged] = useState(false);

  useEffect(() => {
    if (!rmaId) return;
    getRMA(rmaId).then(({ data }) => setRma(data));
  }, [rmaId]);

  if (!rmaId) return null;

  const act = async (fn, ok) => {
    try {
      const { data } = await fn();
      setRma(data);
      toast.success(ok);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  return (
    <Dialog open={!!rmaId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rma-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono">{rma?.rma_number || 'Loading…'}</span>
            {rma && (
              <span className={`px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase ${STATUS_STYLES[rma.status]}`}>
                {rma.status}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        {rma && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Order:</span> <b>{rma.order_number || '—'}</b></div>
              <div><span className="text-muted-foreground">Customer:</span> <b>{rma.customer_email || '—'}</b></div>
              <div><span className="text-muted-foreground">Reason:</span> <b>{rma.reason}</b></div>
              <div><span className="text-muted-foreground">Method:</span> <b>{rma.return_method}</b></div>
              {rma.pickup_address && <div className="col-span-2"><span className="text-muted-foreground">Pickup:</span> {rma.pickup_address}</div>}
              {rma.dropoff_location_id && <div className="col-span-2"><span className="text-muted-foreground">Drop-off:</span> {rma.dropoff_location_id}</div>}
              {rma.auto_approval_rule && <div className="col-span-2 text-xs text-emerald-700">Auto rule: {rma.auto_approval_rule}</div>}
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Items</h4>
              <ul className="text-sm space-y-1">
                {rma.items.map((i, idx) => (
                  <li key={idx} className="flex justify-between border-b border-border py-1">
                    <span>{i.sku_code || i.name || i.variant_id}</span>
                    <span className="font-mono">x{i.quantity}</span>
                  </li>
                ))}
              </ul>
            </div>

            {rma.attachments?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
                  Attachments ({rma.attachments.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {rma.attachments.map((a, i) => (
                    <a key={i} href={a.data_url} target="_blank" rel="noreferrer" className="border border-border w-20 h-20 block">
                      <img src={a.data_url} alt={a.filename} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Timeline</h4>
              <ol className="text-xs space-y-1.5">
                {rma.timeline?.map((t, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-muted-foreground font-mono shrink-0">{new Date(t.at).toLocaleString()}</span>
                    <span className="font-bold uppercase tracking-wider text-[10px] mt-0.5">{t.status}</span>
                    <span className="text-muted-foreground">· {t.by}{t.note ? ` — ${t.note}` : ''}</span>
                  </li>
                ))}
              </ol>
            </div>

            {rma.status === 'pending' && (
              <div className="border-t border-border pt-4 space-y-2">
                <Textarea rows={2} placeholder="Optional note" value={note} onChange={e => setNote(e.target.value)} data-testid="rma-decision-note" />
                <div className="flex gap-2">
                  <Button className="bg-emerald-700 hover:bg-emerald-800 flex-1" data-testid="rma-approve-btn"
                    onClick={() => act(() => approveRMA(rma.id, { note }), 'Approved')}>
                    <Check size={14} className="mr-1" /> Approve
                  </Button>
                  <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50 flex-1" data-testid="rma-reject-btn"
                    onClick={() => act(() => rejectRMA(rma.id, { note }), 'Rejected')}>
                    <X size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
            )}
            {rma.status === 'approved' && (
              <div className="border-t border-border pt-3 space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Receive &amp; Restock</h4>
                <div>
                  <Label className="text-[11px]">Restock location ID <span className="text-muted-foreground normal-case">(blank = auto)</span></Label>
                  <Input value={restockLocationId} onChange={e => setRestockLocationId(e.target.value)} data-testid="rma-restock-loc" placeholder="Leave empty to auto-pick a returns zone" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-xs">
                  <input type="checkbox" checked={inspectionDamaged} onChange={e => setInspectionDamaged(e.target.checked)} data-testid="rma-inspection-damaged" />
                  <span>Inspected: items are <b>unsalvageable</b> → force quarantine</span>
                </label>
                <p className="text-[10px] text-muted-foreground">
                  {rma.reason === 'damaged'
                    ? 'Default: items go to QUARANTINE (reason = damaged).'
                    : 'Default: items go to AVAILABLE (sellable stock).'}
                  {' '}Linked order balance will be reduced by Σ(unit_price × qty).
                </p>
                <Button
                  onClick={() => act(
                    () => advanceRMAStatus(rma.id, {
                      status: 'received',
                      restock_location_id: restockLocationId || null,
                      inspection_damaged: inspectionDamaged || null,
                    }),
                    'Received · stock returned',
                  )}
                  data-testid="rma-mark-received"
                  className="w-full bg-sky-700 hover:bg-sky-800">
                  <Package size={14} className="mr-1" /> Mark received &amp; restock
                </Button>
              </div>
            )}
            {rma.status === 'received' && rma.restock && (
              <div className="border-t border-border pt-3 space-y-2" data-testid="rma-restock-receipt">
                <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
                  <CheckCircle size={14} weight="fill" /> Stock returned
                </h4>
                <div className="text-xs text-foreground border border-border bg-muted p-2 space-y-0.5">
                  <p>Restock location: <span className="font-mono">{rma.restock.restock_location_id}</span></p>
                  {rma.restock.items?.map((it, i) => (
                    <p key={i} className="flex justify-between">
                      <span>{it.sku_code || it.variant_id}</span>
                      <span className="font-mono">
                        +{it.quantity} →{' '}
                        <span className={it.inventory_status === 'quarantine' ? 'text-amber-700 font-bold' : 'text-emerald-700 font-bold'}>
                          {it.inventory_status}
                        </span>
                      </span>
                    </p>
                  ))}
                  {rma.balance_update?.order_number && (
                    <p className="border-t border-border pt-1 mt-1 text-muted-foreground">
                      Order <b>{rma.balance_update.order_number}</b> · refunded <span className="font-mono">${rma.balance_update.refund_amount?.toFixed(2)}</span>
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => act(() => advanceRMAStatus(rma.id, { status: 'refunded' }), 'Refunded')} className="flex-1 bg-brand hover:bg-[#001F8A]" data-testid="rma-mark-refunded">Refund</Button>
                  <Button variant="outline" onClick={() => act(() => advanceRMAStatus(rma.id, { status: 'completed' }), 'Completed')} className="flex-1" data-testid="rma-mark-completed">Complete</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Returns() {
  const [rmas, setRmas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [statusFilter, setStatusFilter] = useUrlFilterState('status', 'ALL');
  const [searchTerm, setSearchTerm] = useUrlFilterState('q', '');
  const [reasonFilter, setReasonFilter] = useUrlFilterState('reason', '');
  const [dateWindow, setDateWindow] = useUrlFilterState('date', '');
  const clearUrlFilters = useUrlFilterClear(['q', 'status', 'reason', 'date']);
  const rmaSel = useBulkSelection();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: rows }, { data: sum }] = await Promise.all([listRMAs({ limit: 200 }), getRMASummary()]);
      setRmas(rows); setSummary(sum);
    } catch (_) {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rmas.filter(r => {
      if (statusFilter !== 'ALL' && r.status !== statusFilter) return false;
      if (reasonFilter && r.reason !== reasonFilter) return false;
      if (term) {
        const hay = `${r.rma_number || ''} ${r.customer_email || ''} ${r.order_number || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (!matchesCreatedWindow(r.created_at, dateWindow)) return false;
      return true;
    });
  }, [rmas, statusFilter, searchTerm, reasonFilter, dateWindow]);

  const reasonLabel = (r) => REASONS.find(x => x.key === r)?.label || r;

  // Pending RMAs only — Approve/Reject can only act on these.
  const pendingRmas = filtered.filter((r) => r.status === 'pending');

  // Bulk Approve / Reject — iterate through the selected pending ids, fan-out
  // serialised so the toast count is accurate even on a slow connection.
  const runBulkAction = async (label, fn) => {
    const targetIds = rmaSel.ids.filter((id) => {
      const r = rmas.find((x) => x.id === id);
      return r && r.status === 'pending';
    });
    if (targetIds.length === 0) {
      toast.error('Selection contains no pending RMAs.');
      return;
    }
    if (!window.confirm(`${label} ${targetIds.length} RMA${targetIds.length === 1 ? '' : 's'}?`)) return;
    let ok = 0; let failed = 0;
    for (const id of targetIds) {
      try {
        await fn(id);
        ok++;
      } catch (e) {
        console.error(e);
        failed++;
      }
    }
    rmaSel.clear();
    if (failed > 0) toast.error(`${label} ${ok}, ${failed} failed`);
    else toast.success(`${ok} RMA${ok === 1 ? '' : 's'} ${label.toLowerCase()}d`);
    load();
  };
  const handleBulkApprove = () => runBulkAction('Approve', (id) => approveRMA(id, {}));
  const handleBulkReject = () => runBulkAction('Reject', (id) => rejectRMA(id, {}));

  return (
    <Layout>
      <div className="px-6 py-8 max-w-7xl" data-testid="returns-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ArrowUUpLeft size={18} weight="fill" className="text-brand" />
              <span className="text-[10px] font-bold tracking-[0.2em] text-brand uppercase">Customer Service</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Returns / RMAs</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Customer-initiated returns with rule-based auto-approval.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="rma-new-btn" className="bg-brand hover:bg-[#001F8A]">
            <Plus size={16} className="mr-1.5" /> New RMA
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <StatCard label="Total" value={summary?.total} />
          <StatCard label="Pending" value={summary?.pending} accent="amber" />
          <StatCard label="Approved" value={summary?.approved} accent="green" />
          <StatCard label="Rejected" value={summary?.rejected} accent="red" />
          <StatCard label="Received" value={summary?.received} accent="blue" />
          <StatCard label="Refunded / Done" value={(summary?.refunded || 0) + (summary?.completed || 0)} accent="slate" />
        </div>

        <FilterBar
          testId="returns-filter"
          search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search RMA #, customer, order #…' }}
          filters={[
            {
              key: 'reason', value: reasonFilter, onChange: setReasonFilter, label: 'Reason',
              options: [
                { value: '', label: 'All reasons' },
                ...REASONS.map(r => ({ value: r.key, label: r.label })),
              ],
            },
            {
              key: 'date', value: dateWindow, onChange: setDateWindow, label: 'Date window',
              options: CREATED_WINDOW_OPTIONS,
            },
          ]}
          onClear={clearUrlFilters}
        />

        <div className="flex items-center gap-2 flex-wrap mb-4">
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Filter:</span>
          {['ALL', ...STATUSES].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`rma-filter-${s}`}
              className={`px-2.5 py-1 text-[11px] font-bold tracking-wider uppercase border transition-colors ${
                statusFilter === s ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border hover:border-brand'
              }`}>
              {s.replace('_', ' ')}
            </button>
          ))}
        </div>

        {loading ? <p className="text-muted-foreground text-sm">Loading…</p>
         : filtered.length === 0 ? (
          <div className="border-2 border-dashed border-border p-12 text-center text-muted-foreground">
            No RMAs match this filter.
          </div>
         ) : (
          <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-2" data-testid="rmas-mobile-list">
            {filtered.map(r => {
              const selectable = r.status === 'pending';
              return (
                <div
                  key={r.id}
                  onClick={(e) => {
                    // Don't open detail when clicking the checkbox itself
                    if (e.target.closest('[data-rma-checkbox]')) return;
                    setDetailId(r.id);
                  }}
                  className={`border ${rmaSel.isSelected(r.id) ? 'border-brand bg-blue-50/40' : 'border-border bg-card'} rounded-md p-3 cursor-pointer hover:bg-muted active:bg-muted`}
                  data-testid={`rma-mobile-card-${r.rma_number}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-start gap-2 min-w-0">
                      {selectable && (
                        <span data-rma-checkbox onClick={(e) => e.stopPropagation()} className="pt-0.5">
                          <Checkbox
                            data-testid={`rma-mobile-checkbox-${r.rma_number}`}
                            checked={rmaSel.isSelected(r.id)}
                            onCheckedChange={() => rmaSel.toggle(r.id)}
                            aria-label={`Select RMA ${r.rma_number}`}
                          />
                        </span>
                      )}
                      <span className="font-mono font-bold text-brand text-sm truncate">{r.rma_number}</span>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase ${STATUS_STYLES[r.status]}`}>
                      {r.status}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <div className="flex justify-between gap-2">
                      <span className="truncate">{r.customer_email || '—'}</span>
                      <span className="font-mono shrink-0">{r.order_number || '—'}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span>{reasonLabel(r.reason)} · {r.return_method.replace('_', ' ')}</span>
                      <span className="shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table view */}
          <div className="border border-border bg-card overflow-x-auto hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted border-b border-border">
                <tr className="text-left text-[10px] font-bold tracking-wider uppercase text-muted-foreground">
                  <th className="px-3 py-2 w-10">
                    {pendingRmas.length > 0 && (
                      <Checkbox
                        data-testid="select-all-pending-rmas"
                        aria-label="Select all pending RMAs"
                        checked={rmaSel.isAllSelected(pendingRmas)}
                        onCheckedChange={() => rmaSel.toggleAll(pendingRmas)}
                      />
                    )}
                  </th>
                  <th className="px-3 py-2">RMA #</th>
                  <th className="px-3 py-2">Order</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Method</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr
                    key={r.id}
                    className={`border-b border-border hover:bg-muted cursor-pointer ${rmaSel.isSelected(r.id) ? 'bg-blue-50/40' : ''}`}
                    onClick={(e) => {
                      if (e.target.closest('[data-rma-checkbox]')) return;
                      setDetailId(r.id);
                    }}
                    data-testid={`rma-row-${r.rma_number}`}
                  >
                    <td className="px-3 py-2">
                      {r.status === 'pending' && (
                        <span data-rma-checkbox onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            data-testid={`rma-checkbox-${r.rma_number}`}
                            checked={rmaSel.isSelected(r.id)}
                            onCheckedChange={() => rmaSel.toggle(r.id)}
                            aria-label={`Select RMA ${r.rma_number}`}
                          />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono font-bold text-brand">{r.rma_number}</td>
                    <td className="px-3 py-2 font-mono">{r.order_number || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.customer_email || '—'}</td>
                    <td className="px-3 py-2">{reasonLabel(r.reason)}</td>
                    <td className="px-3 py-2 text-muted-foreground text-xs uppercase tracking-wider">{r.return_method.replace('_', ' ')}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase ${STATUS_STYLES[r.status]}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2 text-right"><CaretRight size={14} className="text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
         )}

        <CreateRMADialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
        <RMADetailDialog rmaId={detailId} onOpenChange={(v) => { if (!v) setDetailId(null); }} onUpdated={load} />

        {/* Bulk-actions bar — visible when ≥1 pending RMA selected. Approve / Reject. */}
        <BulkActionsBar
          count={rmaSel.count}
          onClear={rmaSel.clear}
          actions={[
            {
              key: 'approve',
              label: 'Approve',
              icon: <Check size={14} weight="bold" />,
              onClick: handleBulkApprove,
              testId: 'bulk-approve-rmas-btn',
              title: `Approve ${rmaSel.count} pending RMA${rmaSel.count === 1 ? '' : 's'}`,
            },
            {
              key: 'reject',
              label: 'Reject',
              icon: <XCircle size={14} weight="fill" />,
              onClick: handleBulkReject,
              testId: 'bulk-reject-rmas-btn',
              title: `Reject ${rmaSel.count} pending RMA${rmaSel.count === 1 ? '' : 's'}`,
            },
          ]}
        />
      </div>
    </Layout>
  );
}
